import type { MemberRole } from './enums.js';

/**
 * Registre fermé des widgets du tableau de bord. C'est LA source : le serveur valide la disposition
 * enregistrée contre cette liste, le front en tire son rendu et son personnalisateur.
 *
 * Écart assumé avec la V1 (`lib/dashboardWidgetRegistry.ts`, 19 widgets) : les cinq widgets marqués
 * `implemented:false` mais rendus quand même ont disparu. `consultations-evolution` et
 * `revenue-trend` reviennent, mais branchés sur les séries réelles de `GET /api/dashboard/charts`
 * au lieu d'un `Math.random()` et de multiplicateurs en dur ; `sync-status` et `quick-actions`
 * sortent (respectivement pas de module d'intégration, barre d'actions déjà dans l'en-tête de
 * page). `pediatric-kpis` revient, calculé sur les bilans pédiatriques du cabinet là où la V1
 * codait deux indicateurs sur trois en dur ; il est masqué par défaut — la plupart des cabinets
 * n'ont pas de pédiatrie — et s'affiche depuis la personnalisation. `taux-remplissage`,
 * `duree-moyenne-traitement` et `patients-reguliers` reviennent visibles par défaut, calculés par
 * `GET /api/dashboard/operations`. `taches` revient sur la table `personal_tasks` : les tâches du
 * membre, et non plus l'heuristique de la V1, qui lisait les dossiers de tous les cabinets. Voir
 * docs/audit/audit-features.md §4.4.
 *
 * Aucun libellé ici : le front traduit `dashboard.widgets.<id>`.
 */

export const dashboardWidgetSections = [
  'activite',
  'finances',
  'clinique',
  'operations',
  'demographie',
  'intelligence',
] as const;
export type DashboardWidgetSection = (typeof dashboardWidgetSections)[number];

export const dashboardWidgetIds = [
  /* Activité */
  'rendez-vous-du-jour',
  'taches',
  'anniversaires',
  'rendez-vous-a-venir',
  'patients-actifs',
  'consultations-ce-mois',
  'nouveaux-patients',
  'consultations-evolution',
  /* Finances */
  'revenu-overview',
  'revenue-trend',
  'impayes',
  'relances-en-attente',
  /* Clinique */
  'top-motifs',
  'brouillons-consultation',
  'duree-moyenne-traitement',
  'pediatric-kpis',
  /* Opérations */
  'taux-remplissage',
  'patients-reguliers',
  'repartition-statuts-rdv',
  /* Démographie */
  'repartition-sexe',
  'repartition-age',
  'sources-recommandation',
  /* Intelligence */
  'indications',
] as const;
export type DashboardWidgetId = (typeof dashboardWidgetIds)[number];

export interface DashboardWidgetDefinition {
  readonly id: DashboardWidgetId;
  readonly section: DashboardWidgetSection;
  /**
   * Rôles autorisés à voir le widget. Absent = tout membre du cabinet. La V1 portait des drapeaux
   * `premium` que rien n'appliquait ; ici le prérequis est lisible et le serveur s'en sert pour
   * construire la disposition par défaut.
   */
  readonly roles?: readonly MemberRole[];
  /** Largeur initiale en colonnes de la grille, 1 à 4. */
  readonly defaultWidth: 1 | 2 | 3 | 4;
  /** Absent de la grille tant que l'utilisateur ne l'affiche pas depuis la personnalisation. */
  readonly hiddenByDefault?: boolean;
}

/** Le chiffre d'affaires du cabinet ne regarde que celui qui l'encaisse. */
const FINANCE_ROLES: readonly MemberRole[] = ['owner'];

export const dashboardWidgets: readonly DashboardWidgetDefinition[] = [
  { id: 'rendez-vous-du-jour', section: 'activite', defaultWidth: 2 },
  /** Visible par défaut, juste après la journée, comme dans la V1. Chacun y voit ses propres tâches. */
  { id: 'taches', section: 'activite', defaultWidth: 2 },
  /** Demande de Nicolas, 11 octobre 2026 : anniversaires de la semaine et courriel de vœux. */
  { id: 'anniversaires', section: 'activite', defaultWidth: 2 },
  { id: 'rendez-vous-a-venir', section: 'activite', defaultWidth: 1 },
  { id: 'patients-actifs', section: 'activite', defaultWidth: 1 },
  { id: 'consultations-ce-mois', section: 'activite', defaultWidth: 1 },
  { id: 'nouveaux-patients', section: 'activite', defaultWidth: 1 },
  { id: 'consultations-evolution', section: 'activite', defaultWidth: 2 },
  { id: 'revenu-overview', section: 'finances', roles: FINANCE_ROLES, defaultWidth: 2 },
  { id: 'revenue-trend', section: 'finances', roles: FINANCE_ROLES, defaultWidth: 2 },
  { id: 'impayes', section: 'finances', roles: FINANCE_ROLES, defaultWidth: 1 },
  { id: 'relances-en-attente', section: 'finances', roles: FINANCE_ROLES, defaultWidth: 1 },
  { id: 'top-motifs', section: 'clinique', defaultWidth: 2 },
  { id: 'brouillons-consultation', section: 'clinique', defaultWidth: 1 },
  /** Visible par défaut, comme en V1 (section Clinique) : moyenne de séances par patient suivi. */
  { id: 'duree-moyenne-traitement', section: 'clinique', defaultWidth: 1 },
  { id: 'pediatric-kpis', section: 'clinique', defaultWidth: 2, hiddenByDefault: true },
  /** Visible par défaut : la V1 l'affichait d'office dans Opérationnel. */
  { id: 'taux-remplissage', section: 'operations', defaultWidth: 1 },
  { id: 'patients-reguliers', section: 'operations', defaultWidth: 1 },
  { id: 'repartition-statuts-rdv', section: 'operations', defaultWidth: 2 },
  { id: 'repartition-sexe', section: 'demographie', defaultWidth: 1 },
  { id: 'repartition-age', section: 'demographie', defaultWidth: 2 },
  { id: 'sources-recommandation', section: 'demographie', defaultWidth: 1 },
  { id: 'indications', section: 'intelligence', defaultWidth: 2 },
];

const widgetsById = new Map<DashboardWidgetId, DashboardWidgetDefinition>(
  dashboardWidgets.map((widget) => [widget.id, widget]),
);

export function findDashboardWidget(id: DashboardWidgetId): DashboardWidgetDefinition {
  const widget = widgetsById.get(id);
  // La carte est construite à partir de la liste fermée : l'identifiant est forcément connu.
  if (!widget) throw new Error(`widget de tableau de bord inconnu : ${id}`);
  return widget;
}

export function isDashboardWidgetAllowed(
  widget: DashboardWidgetDefinition,
  role: MemberRole,
): boolean {
  return widget.roles === undefined || widget.roles.includes(role);
}
